import React, { useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './components/shared/ScrollToTop';
import Loader from './components/shared/Loader';
import LoginContainer from './components/shared/users/LoginContainer';
import Register from './components/shared/users/Register';
import Profile from './components/shared/users/Profile';
import AdminLogin from './components/admin/AdminLogin';
import PrivateRoute from './components/shared/PrivateRoute';
import Dashboard from './components/admin/Dashboard';
import Orders from './components/admin/Orders';
import Inicio from './components/client/Inicio';
import ProductList from './components/client/ProductList';
import SingleProduct from './components/client/SingleProduct';
import ProductoAIList from './components/client/ProductoAIList';
import Cart from './components/client/Cart';
import Checkout from './components/client/Checkout';
import OrderSuccess from './components/client/OrderSuccess';
import OrderFailure from './components/client/OrderFailure';
import OrderPending from './components/client/OrderPending';
import Contact from './components/client/Contact';
import Menu from './components/client/Menu';
import Footer from './components/client/Footer';
import CategoryLanding from './components/client/CategoryLanding';
import { AboutPage, FaqPage, WholesalePage } from './components/client/InformationalPages';
import ChatWidget from './components/shared/ChatWidget';
import WhatsAppButton from './components/client/WhatsAppButton';
import MaintenancePage from './components/client/MaintenancePage';
import { NoIndex } from './components/shared/SEO';
import { AuthContext } from './providers/AuthContext';
import { CategoryProvider, CategoryContext } from './providers/CategoryContext';
import { ConfigProvider } from './providers/ConfigContext';
import MetaPixelTracker from './components/shared/MetaPixelTracker';

function MaintenanceGuard({ isAdminAuthenticated, children }) {
  const [maintenance, setMaintenance] = useState(false);
  const [checked, setChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/store-config`)
      .then(r => r.json())
      .then(data => {
        setMaintenance(data?.data?.maintenanceMode === true);
      })
      .catch(() => {})
      .finally(() => setChecked(true));
  }, []);

  const isAdminPath = location.pathname.startsWith('/admin')
    || location.pathname.startsWith('/dashboard')
    || location.pathname.startsWith('/orders');

  if (!checked) return null;
  if (maintenance && !isAdminAuthenticated && !isAdminPath) return <><NoIndex /><MaintenancePage /></>;
  return children;
}

function NoIndexPage({ children }) {
  return (
    <>
      <NoIndex />
      {children}
    </>
  );
}

function AppContent() {
  const { user, isAdminAuthenticated } = useContext(AuthContext);
  const { loading: categoriesLoading } = useContext(CategoryContext);

  // Esperamos a que las categorías carguen para armar el menú completo
  if (categoriesLoading) return <Loader />;

  return (
    <Router>
      <ScrollToTop />
      <MetaPixelTracker />
      <MaintenanceGuard isAdminAuthenticated={isAdminAuthenticated}>
        <Menu isAdminAuthenticated={isAdminAuthenticated} />
        <Routes>
          <Route path="/" element={<Inicio />} />
          <Route path="/tienda" element={<ProductList />} />
          <Route path="/mujer" element={<CategoryLanding slug="mujer" />} />
          <Route path="/hombre" element={<CategoryLanding slug="hombre" />} />
          <Route path="/ninos" element={<CategoryLanding slug="ninos" />} />
          <Route path="/ropa-deportiva" element={<CategoryLanding slug="ropa-deportiva" />} />
          <Route path="/nosotros" element={<AboutPage />} />
          <Route path="/mayoristas" element={<WholesalePage />} />
          <Route path="/preguntas-frecuentes" element={<FaqPage />} />
          <Route path="/productoai/:clientId" element={<NoIndexPage><ProductoAIList /></NoIndexPage>} />
          <Route path="/cart" element={<NoIndexPage><Cart /></NoIndexPage>} />
          <Route path="/product/:id" element={<SingleProduct />} />
          <Route path="/checkout" element={<NoIndexPage><Checkout /></NoIndexPage>} />
          <Route path="/success" element={<NoIndexPage><OrderSuccess /></NoIndexPage>} />
          <Route path="/orden-confirmada" element={<NoIndexPage><OrderSuccess /></NoIndexPage>} />
          <Route path="/failure" element={<NoIndexPage><OrderFailure /></NoIndexPage>} />
          <Route path="/orden-fallida" element={<NoIndexPage><OrderFailure /></NoIndexPage>} />
          <Route path="/pending" element={<NoIndexPage><OrderPending /></NoIndexPage>} />
          <Route path="/orden-pendiente" element={<NoIndexPage><OrderPending /></NoIndexPage>} />
          <Route path="/contacto" element={<Contact />} />

          {/* Admin */}
          <Route path="/admin" element={<NoIndexPage><AdminLogin /></NoIndexPage>} />

          {/* Usuarios */}
          <Route path="/user" element={<NoIndexPage><LoginContainer /></NoIndexPage>} />
          <Route path="/register" element={<NoIndexPage><Register /></NoIndexPage>} />
          <Route
            path="/profile"
            element={user ? <NoIndexPage><Profile /></NoIndexPage> : <Navigate to="/user" replace />}
          />

          {/* Rutas protegidas Admin */}
          <Route element={<PrivateRoute isAuthenticated={isAdminAuthenticated} />}>
            <Route path="/dashboard" element={<NoIndexPage><Dashboard /></NoIndexPage>} />
            <Route path="/orders" element={<NoIndexPage><Orders /></NoIndexPage>} />
          </Route>
        </Routes>
        <Footer />
        <WhatsAppButton />
        <ChatWidget />
      </MaintenanceGuard>
    </Router>
  );
}

function App() {
  const { loading } = useContext(AuthContext);

  // Esperamos a que Firebase confirme el estado
  if (loading) return <Loader />;

  return (
    <ConfigProvider>
      <CategoryProvider>
        <AppContent />
      </CategoryProvider>
    </ConfigProvider>
  );
}

export default App;
